import { useState, useEffect, useRef, useContext } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  FaStar,
  FaUser,
  FaCertificate,
  FaClock,
  FaArrowLeft,
  FaSpinner,
  FaSignInAlt,
  FaUserPlus,
  FaCheckCircle,
  FaBook,
  FaMedal,
  FaLanguage,
  FaRupeeSign,
  FaTag,
  FaPercent,
  FaCreditCard,
  FaBell,
  FaUserTie,
  FaTimesCircle,
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaCity,
  FaHome,
  FaEnvelope,
  FaPhone,
  FaSave,
  FaTimes
} from 'react-icons/fa';
import TrainerAvailabilityNotificationModal from '../Components/TrainerAvailabilityNotificationModal';
import HttpInterceptor from '../lib/HttpInterceptor';
import { toast } from 'react-toastify';
import Avatar from '../Components/Avatar';
import StarRating from '../Components/StarRating';
import LandingHeader from '../Components/LandingHeader';
import Footer from '../Components/Footer';
import UserContext from '../Components/UserContext';
import { useSWRConfig } from 'swr';
import { formatCurrency } from '../utils/currencyFormatter';
import { formatName, toTitleCase } from '../utils/dataTransformers';

function PublicTrainerProfile() {
  const { trainerId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { session, setSession } = useContext(UserContext);
  const { mutate } = useSWRConfig();
  const isStudentRoute = location.pathname.startsWith('/student/');

  // Determine if user is logged in
  // Since this is a public page (not wrapped in Guard), session might not be initialized in UserContext
  // However, if the route starts with /student/, the user must be logged in (those routes are protected)
  // Also check session from UserContext if available
  const isLoggedIn = !!(session && session !== null && typeof session === 'object' && Object.keys(session).length > 0) || isStudentRoute;
  const [trainer, setTrainer] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [isPWA, setIsPWA] = useState(false);

  // Refs to prevent duplicate calls
  const abortControllerRef = useRef(null);
  const hasFetchedRef = useRef(false);
  const lastTrainerIdRef = useRef(null);
  const errorShownRef = useRef(false);
  const currentRequestRef = useRef(null); // Track the current request to prevent aborting it

  // Detect PWA standalone mode
  useEffect(() => {
    const checkPWA = () => {
      const standalone = window.matchMedia('(display-mode: standalone)').matches ||
        window.navigator.standalone === true;
      setIsPWA(standalone);
    };
    checkPWA();
    window.addEventListener('resize', checkPWA);
    return () => window.removeEventListener('resize', checkPWA);
  }, []);

  useEffect(() => {
    if (!trainerId || trainerId.trim() === '') {
      console.error('[PublicTrainerProfile] No trainerId provided:', trainerId);
      setError('Trainer ID is required');
      setIsLoading(false);
      return;
    }

    // Validate trainerId format (should be at least 10 characters, typically 24 for MongoDB ObjectId)
    if (trainerId.length < 10) {
      console.error('[PublicTrainerProfile] Invalid trainerId format:', trainerId);
      setError('Invalid trainer ID format');
      setIsLoading(false);
      return;
    }

    // Reset fetch flag if trainerId changed
    if (lastTrainerIdRef.current !== trainerId) {
      lastTrainerIdRef.current = trainerId;
      hasFetchedRef.current = false;
      errorShownRef.current = false;
      setError(null);
      setTrainer(null);
    }

    if (hasFetchedRef.current && lastTrainerIdRef.current === trainerId) {
      return;
    }

    // Create new AbortController for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    const requestId = `${trainerId}-${Date.now()}`;
    currentRequestRef.current = requestId;

    const fetchTrainerProfile = async () => {
      try {
        setIsLoading(true);
        setError(null);
        errorShownRef.current = false;

        // Clean trainerId - remove any invalid characters
        const cleanTrainerId = trainerId.replace(/[^a-zA-Z0-9-]/g, '');

        const response = await HttpInterceptor.get(`/api/trainers/${cleanTrainerId}`, {
          showErrorToast: false,
          signal: abortController.signal
        });

        // Check if request was aborted or superseded
        if (abortController.signal.aborted || currentRequestRef.current !== requestId) {
          return;
        }

          if (response && response.status === 200) {
          const trainerData = response.data?.data || response.data;

          if (!trainerData) {
            throw new Error('No trainer data received');
          }

          // Determine if this is a consultant profile
          // Check if any program has consultant-specific fields (timeWindowStart, slotDurations)
          const hasConsultantPrograms = trainerData.programs?.some(program =>
            program.timeWindowStart || program.slotDurations || Array.isArray(program.slotDurations) || program.partnerType === 'consultant'
          );

          // Check trainer's servicePartnerTypes if available
          const trainerServices = (trainerData.services && trainerData.services.length > 0) ? trainerData.services : (trainerData.service ? [trainerData.service] : []);
          const servicePartnerTypes = trainerData.servicePartnerTypes || {};
          const activeService = trainerServices[0] || trainerData.service || '';

          // Check partnerType at multiple levels
          let activePartnerType = activeService
            ? (servicePartnerTypes[activeService] || trainerData.partnerType || 'instructor')
            : (trainerData.partnerType || 'instructor');

          // Also check if any program has partnerType === 'consultant'
          const hasConsultantProgramType = trainerData.programs?.some(program => program.partnerType === 'consultant');

          // Fallback: If partnerType/servicePartnerTypes are not available from backend,
          // check if programs have consultant-style structure (duration: 1, classesPerDuration: 1)
          // These are placeholder values used for consultant programs
          let fallbackConsultant = false;
          if (!trainerData.partnerType && !servicePartnerTypes[activeService] && trainerData.programs?.length > 0) {
            // Check if all programs have the placeholder values (1/1) typical of consultant programs
            const allProgramsHavePlaceholderValues = trainerData.programs.every(program =>
              (program.duration === 1 || program.duration === undefined) &&
              (program.classesPerDuration === 1 || program.classesPerDuration === undefined)
            );

            // Check if programs lack instructor-specific fields that would indicate real instructor programs
            const hasRealInstructorPrograms = trainerData.programs.some(program =>
              program.duration && program.duration > 1 && program.classesPerDuration && program.classesPerDuration > 1
            );

            // If all programs have placeholder values and none have real instructor structure, assume consultant
            if (allProgramsHavePlaceholderValues && !hasRealInstructorPrograms) {
              fallbackConsultant = true;
              activePartnerType = 'consultant';
            }
          }

          const isConsultant = activePartnerType === 'consultant' || hasConsultantPrograms || trainerData.partnerType === 'consultant' || hasConsultantProgramType || fallbackConsultant;

          // Store consultant status in trainer data
          trainerData.isConsultant = isConsultant;

          setTrainer(trainerData);
          setIsLoading(false);
          hasFetchedRef.current = true; // Mark as fetched only after success
        } else {
          throw new Error(`Unexpected status: ${response?.status}`);
        }
      } catch (err) {
        // Ignore abort errors
        if (err.name === 'AbortError' || err.code === 'ERR_CANCELED' || abortController.signal.aborted || currentRequestRef.current !== requestId) {
          return; // Don't update state for aborted requests
        }

        // Only log and show error once per fetch attempt
        if (!errorShownRef.current) {
          errorShownRef.current = true;

          // Debug logging
          if (import.meta.env.DEV) {
            console.error('[PublicTrainerProfile] Error fetching trainer profile:', {
              error: err,
              message: err.message,
              type: err.type,
              code: err.code,
              response: err.response,
              responseStatus: err.response?.status,
              responseData: err.response?.data
            });
          }

          let errorMessage = 'Failed to load trainer profile. Please try again later.';
          if (err.response?.status === 404) {
            errorMessage = 'Trainer profile not found.';
          } else if (err.response?.data?.message) {
            errorMessage = err.response.data.message;
          } else if (err.message) {
            errorMessage = err.message;
          }

          setError(errorMessage);
          setIsLoading(false);
          hasFetchedRef.current = true; // Mark as fetched even on error to prevent infinite retries

          // Always show toast for errors
          toast.error(errorMessage);
        }
      } finally {
        // Always clear loading state if request wasn't aborted
        if (!abortController.signal.aborted && currentRequestRef.current === requestId) {
          // Loading state is already set in try/catch, but ensure it's cleared
          // This is a safety net
        }
      }
    };

    fetchTrainerProfile();

    // Cleanup: abort request only if service name changed or component unmounts
    return () => {
      // Only abort if this is still the current request (not superseded by a new one)
      if (abortControllerRef.current && currentRequestRef.current === requestId) {
        abortControllerRef.current.abort();
        currentRequestRef.current = null;
      }
    };
  }, [trainerId]);

  const [selectedProgramIds, setSelectedProgramIds] = useState([]);
  const [referralCode, setReferralCode] = useState('');

  // Booking modal state for consultant programs
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [bookingProgram, setBookingProgram] = useState(null);
  const [selectedSlotDuration, setSelectedSlotDuration] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [isBooking, setIsBooking] = useState(false);
  const [existingAppointments, setExistingAppointments] = useState([]);
  const [timeConflict, setTimeConflict] = useState(null);
  const [loadingAppointments, setLoadingAppointments] = useState(false);

  // Check for referral code and pre-selected programs in URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const refParam = params.get('ref');
    if (refParam) {
      setReferralCode(refParam.toUpperCase().trim());
    }

    // Pre-select programs from URL if programId is present
    const programIds = params.getAll('programId');
    if (programIds && programIds.length > 0) {
      setSelectedProgramIds(prev => {
        // Only update if not already set (to avoid infinite loop or overwriting user interactions)
        if (prev.length === 0) {
          return programIds;
        }
        return prev;
      });
    }
  }, []);

  // Quick Profile Completion State
  const [showQuickProfileModal, setShowQuickProfileModal] = useState(false);
  const [quickProfileData, setQuickProfileData] = useState({
    mobile: '',
    gender: 'male',
    addressLine1: '',
    city: '',
    state: '',
    pincode: ''
  });
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);

  // Sync quick profile data with session if session exists but incomplete
  useEffect(() => {
    if (session && !isProfileComplete(session)) {
      setQuickProfileData({
        mobile: session.mobile || '',
        gender: session.gender || 'male',
        addressLine1: session.address?.addressLine1 || '',
        city: session.address?.city || '',
        state: session.address?.state || '',
        pincode: session.address?.pincode || ''
      });
    }
  }, [session]);

  const isProfileComplete = (sess) => {
    return !!(
      sess?.mobile && 
      sess?.gender && 
      sess?.address?.addressLine1 && 
      sess?.address?.city && 
      sess?.address?.state && 
      sess?.address?.pincode
    );
  };

  // Fetch existing appointments for conflict checking
  useEffect(() => {
    const fetchAppointments = async () => {
      if (!bookingProgram || !selectedDate || !trainerId) {
        setExistingAppointments([]);
        setTimeConflict(null);
        return;
      }

      try {
        setLoadingAppointments(true);
        // Fetch appointments for the selected date
        const startDate = new Date(selectedDate);
        startDate.setHours(0, 0, 0, 0);
        const endDate = new Date(selectedDate);
        endDate.setHours(23, 59, 59, 999);

        const response = await HttpInterceptor.get('/api/appointments', {
          params: {
            trainerId: trainerId,
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
            status: 'pending,approved,confirmed' // Only check active appointments
          }
        });

        if (response.status === 200) {
          const appointments = response.data?.data || [];
          setExistingAppointments(appointments);
        }
      } catch (error) {
        console.error('Error fetching appointments:', error);
        setExistingAppointments([]);
      } finally {
        setLoadingAppointments(false);
      }
    };

    fetchAppointments();
  }, [bookingProgram, selectedDate, trainerId]);

  // Check for time conflicts when time or slot duration changes
  useEffect(() => {
    if (!selectedDate || !selectedTime || !selectedSlotDuration || existingAppointments.length === 0) {
      setTimeConflict(null);
      return;
    }

    // Parse slot duration to minutes
    const parseDurationToMinutes = (duration) => {
      if (!duration) return 0;
      const durationStr = String(duration).toLowerCase();

      // Handle formats like "30 mins", "1 hour", "1.5 hours", "2 hours"
      const minsMatch = durationStr.match(/(\d+(?:\.\d+)?)\s*mins?/);
      if (minsMatch) {
        return Math.round(parseFloat(minsMatch[1]));
      }

      const hoursMatch = durationStr.match(/(\d+(?:\.\d+)?)\s*hours?/);
      if (hoursMatch) {
        return Math.round(parseFloat(hoursMatch[1]) * 60);
      }

      // Default fallback: try to parse as number (assuming minutes)
      const numMatch = durationStr.match(/(\d+)/);
      if (numMatch) {
        return parseInt(numMatch[1], 10);
      }

      return 0;
    };

    const slotDurationMinutes = parseDurationToMinutes(selectedSlotDuration);
    if (slotDurationMinutes === 0) {
      setTimeConflict(null);
      return;
    }

    // Calculate appointment start and end time
    const appointmentStart = new Date(`${selectedDate}T${selectedTime}`);
    const appointmentEnd = new Date(appointmentStart.getTime() + slotDurationMinutes * 60000);

    // Check conflicts with 15-minute margin
    const marginMinutes = 15;
    const marginMs = marginMinutes * 60000;

    const conflict = existingAppointments.find(apt => {
      if (!apt.requestedDateTime && !apt.appointmentDate) return false;

      const existingStart = new Date(apt.requestedDateTime || apt.appointmentDate);
      const existingEnd = new Date(existingStart);

      // Calculate existing appointment end time
      const existingSlotDuration = apt.slotDuration || selectedSlotDuration;
      const existingDurationMinutes = parseDurationToMinutes(existingSlotDuration);
      existingEnd.setMinutes(existingEnd.getMinutes() + existingDurationMinutes);

      // Check if there's an overlap (with 15-minute margin)
      // New appointment should start at least 15 minutes after existing appointment ends
      // OR existing appointment should start at least 15 minutes after new appointment ends
      const newStartWithMargin = new Date(appointmentStart.getTime() - marginMs);
      const newEndWithMargin = new Date(appointmentEnd.getTime() + marginMs);

      return (
        (existingStart >= newStartWithMargin && existingStart <= newEndWithMargin) ||
        (existingEnd >= newStartWithMargin && existingEnd <= newEndWithMargin) ||
        (existingStart <= newStartWithMargin && existingEnd >= newEndWithMargin)
      );
    });

    if (conflict) {
      const conflictStart = new Date(conflict.requestedDateTime || conflict.appointmentDate);
      const conflictEnd = new Date(conflictStart);
      const conflictDurationMinutes = parseDurationToMinutes(conflict.slotDuration || selectedSlotDuration);
      conflictEnd.setMinutes(conflictEnd.getMinutes() + conflictDurationMinutes);

      // Suggest next available time (15 minutes after conflict ends)
      const nextAvailable = new Date(conflictEnd.getTime() + (marginMinutes * 60000));
      const nextAvailableTime = nextAvailable.toTimeString().slice(0, 5);

      const conflictStartTime = conflictStart.toTimeString().slice(0, 5);
      const conflictEndTime = conflictEnd.toTimeString().slice(0, 5);

      setTimeConflict({
        message: `Consultant already has an appointment from ${conflictStartTime} to ${conflictEndTime}. Please book an appointment from ${nextAvailableTime}`,
        conflictStart: conflictStartTime,
        conflictEnd: conflictEndTime,
        nextAvailable: nextAvailableTime
      });
    } else {
      setTimeConflict(null);
    }
  }, [selectedDate, selectedTime, selectedSlotDuration, existingAppointments]);

  const handleEnroll = async () => {
    // Check if user is logged in
    if (!isLoggedIn) {
      // Redirect to signup/login with trainerId and programIds to preserve intent
      const params = new URLSearchParams();
      params.set('trainerId', trainerId || '');
      // Add all selected program IDs
      selectedProgramIds.forEach((id) => {
        params.append('programId', id);
      });
      navigate(`/signup?${params.toString()}`);
      return;
    }

    if (selectedProgramIds.length === 0) {
      toast.error('Please select at least one program to enroll');
      return;
    }

    if (isLoggedIn && !isProfileComplete(session)) {
      setShowQuickProfileModal(true);
      return;
    }

    // Check if this is a consultant program - if so, show booking modal
    if (trainer?.isConsultant && selectedProgramIds.length > 0) {
      // For consultant programs, show booking modal for first selected program
      const programId = selectedProgramIds[0];
      const program = trainer.programs?.find(p => p.id === programId);
      if (program) {
        setBookingProgram(program);
        setShowBookingModal(true);
        // Reset form state
        setSelectedDate('');
        setSelectedTime('');
        setExistingAppointments([]);
        setTimeConflict(null);
        // Initialize with first available slot duration
        if (program.slotPrices && Object.keys(program.slotPrices).length > 0) {
          setSelectedSlotDuration(Object.keys(program.slotPrices)[0]);
        } else if (program.slotDurations && Array.isArray(program.slotDurations) && program.slotDurations.length > 0) {
          setSelectedSlotDuration(program.slotDurations[0]);
        } else if (program.slotDurations && typeof program.slotDurations === 'object') {
          setSelectedSlotDuration(Object.keys(program.slotDurations)[0]);
        }
        return;
      }
    }

    // For instructor programs, proceed with enrollment
    try {
      setIsLoading(true);

      // Enroll in each selected program
      const enrollmentResults = await Promise.allSettled(
        selectedProgramIds.map(programId =>
          HttpInterceptor.post('/api/students/enroll', {
            programId: programId,
            trainerId: trainerId,
            paymentPlanId: null, // Can be enhanced to support payment plan selection
            referralCode: referralCode || undefined // Include referral code if provided
          })
        )
      );

      const successful = [];
      const failed = [];

      enrollmentResults.forEach((result, index) => {
        if (result.status === 'fulfilled' && result.value.status === 201) {
          successful.push({
            programId: selectedProgramIds[index],
            data: result.value.data
          });
        } else {
          const error = result.status === 'rejected' ? result.reason : null;
          const errorMessage = error?.response?.data?.message || 'Enrollment failed';
          failed.push({
            programId: selectedProgramIds[index],
            error: errorMessage
          });
        }
      });

      // Show appropriate messages
      if (successful.length > 0 && failed.length === 0) {
        toast.success(`Successfully enrolled in ${successful.length} program${successful.length > 1 ? 's' : ''}`);
        // Navigate to plans page to see enrolled programs
        navigate('/student/plans');
      } else if (successful.length > 0 && failed.length > 0) {
        toast.warning(
          `Enrolled in ${successful.length} program${successful.length > 1 ? 's' : ''}, ` +
          `${failed.length} program${failed.length > 1 ? 's' : ''} ${failed.length > 1 ? 'were' : 'was'} already enrolled or failed`
        );
        navigate('/student/plans');
      } else {
        const allAlreadyEnrolled = failed.every(f => f.error.includes('Already enrolled'));
        if (allAlreadyEnrolled) {
          toast.info(`You are already enrolled in ${failed.length} program${failed.length > 1 ? 's' : ''}. Please check your enrolled programs.`);
        } else {
          const errorMessages = failed.map(f => f.error).join(', ');
          toast.error(errorMessages || 'All enrollments failed. Please try again.');
        }
      }
    } catch (error) {
      console.error('Error enrolling in programs:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to enroll in programs';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBookAppointment = async () => {
    if (!bookingProgram || !selectedSlotDuration || !selectedDate || !selectedTime) {
      toast.error('Please fill in all booking details');
      return;
    }

    // Check if user has a complete profile (required for student details and consultant contact)
    const isProfileComplete = 
      session?.mobile && 
      session?.gender && 
      session?.address?.addressLine1 && 
      session?.address?.city && 
      session?.address?.state && 
      session?.address?.pincode;

    if (isLoggedIn && !isProfileComplete) {
      toast.info('Please complete your profile (Mobile, Gender, and Address) before booking so consultants can understand you better.', {
        autoClose: 6000
      });
      setTimeout(() => {
        navigate('/student/profile');
      }, 2000);
      return;
    }

    // Validate date (not in the past)
    const selectedDateTime = new Date(`${selectedDate}T${selectedTime}`);
    const now = new Date();
    if (selectedDateTime < now) {
      toast.error('Please select a future date and time');
      return;
    }

    // Validate time window if available
    if (bookingProgram.timeWindowStart && bookingProgram.timeWindowEnd) {
      const [startHour, startMin] = bookingProgram.timeWindowStart.split(':').map(Number);
      const [endHour, endMin] = bookingProgram.timeWindowEnd.split(':').map(Number);
      const [selectedHour, selectedMin] = selectedTime.split(':').map(Number);

      const selectedTimeMinutes = selectedHour * 60 + selectedMin;
      const startTimeMinutes = startHour * 60 + startMin;
      const endTimeMinutes = endHour * 60 + endMin;

      if (selectedTimeMinutes < startTimeMinutes || selectedTimeMinutes >= endTimeMinutes) {
        toast.error(`Time must be between ${bookingProgram.timeWindowStart} and ${bookingProgram.timeWindowEnd}`);
        return;
      }
    }

    // Check for conflicts
    if (timeConflict) {
      toast.error(timeConflict.message);
      return;
    }

    try {
      setIsBooking(true);

      // Combine date and time into ISO datetime string
      const appointmentDateTime = new Date(`${selectedDate}T${selectedTime}`);

      // Validate the date is valid
      if (isNaN(appointmentDateTime.getTime())) {
        toast.error('Invalid date or time selected');
        return;
      }

      const response = await HttpInterceptor.post('/api/appointments', {
        programId: bookingProgram.id,
        slotDuration: selectedSlotDuration,
        requestedDateTime: appointmentDateTime.toISOString()
      });

      if (response.status === 201) {
        toast.success('Appointment booked successfully! The consultant will review and confirm your appointment.');
        setShowBookingModal(false);
        setSelectedProgramIds([]);
        // Reset booking form
        setSelectedSlotDuration('');
        setSelectedDate('');
        setSelectedTime('');
        setBookingProgram(null);
        setExistingAppointments([]);
        setTimeConflict(null);

        // Navigate to appointments dashboard after booking to keep user in logical flow
        if (isStudentRoute || isLoggedIn) {
          navigate('/student/appointments');
        }
      }
    } catch (error) {
      console.error('Error booking appointment:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to book appointment';
      toast.error(errorMessage);
    } finally {
      setIsBooking(false);
    }
  };

  const handleProgramSelect = (programId) => {
    // Toggle program selection (add if not selected, remove if selected)
    setSelectedProgramIds((prev) => {
      if (prev.includes(programId)) {
        // Deselect: remove from array
        return prev.filter((id) => id !== programId);
      } else {
        // Select: add to array
        return [...prev, programId];
      }
    });
  };

  if (isLoading) {
    return (
      <>
        {!isStudentRoute && <LandingHeader />}
        <main className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center">
          <div className="text-center">
            <FaSpinner className="animate-spin text-4xl text-primary mx-auto mb-4" />
            <p className="text-gray-600">Loading trainer profile...</p>
          </div>
        </main>
        {!isStudentRoute && <Footer />}
      </>
    );
  }

  if (error || !trainer) {
    return (
      <>
        {!isStudentRoute && <LandingHeader />}
        <main className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
              <p className="text-red-800">{error || 'Trainer profile not found.'}</p>
              <button
                onClick={() => navigate(-1)}
                className="mt-4 text-primary hover:underline"
              >
                Go back
              </button>
            </div>
          </div>
        </main>
        {!isStudentRoute && <Footer />}
      </>
    );
  }

  // Check if trainer has available (active) programs
  // A program is considered available if:
  // 1. It exists in the programs array
  // 2. It is active (isActive === true) or isActive is undefined (defaults to active)
  const hasAvailablePrograms = trainer.programs &&
    Array.isArray(trainer.programs) &&
    trainer.programs.length > 0 &&
    trainer.programs.some(program => {
      // Consider program available if isActive is true or undefined (defaults to active)
      return program.isActive !== false;
    });

  // If no available programs, show message instead of profile
  if (!hasAvailablePrograms) {
    return (
      <>
        {!isStudentRoute && <LandingHeader />}
        <main className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-8 text-center">
              <div className="max-w-md mx-auto">
                <FaBook className="text-6xl text-gray-300 mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-gray-900 mb-4">No Programs Available</h2>
                <p className="text-gray-600 mb-6">
                  This trainer doesn't have any available programs at the moment.
                  Please check back later or explore other trainers.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button
                    onClick={() => setShowNotificationModal(true)}
                    className="px-6 py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition-colors flex items-center justify-center gap-2"
                  >
                    <FaBell />
                    Notify Me When Available
                  </button>
                  <button
                    onClick={() => navigate(-1)}
                    className="px-6 py-3 bg-gray-200 text-gray-800 rounded-lg font-medium hover:bg-gray-300 transition-colors flex items-center justify-center gap-2"
                  >
                    <FaArrowLeft />
                    Go Back
                  </button>
                  <button
                    onClick={() => navigate('/landing')}
                    className="px-6 py-3 bg-gray-200 text-gray-800 rounded-lg font-medium hover:bg-gray-300 transition-colors"
                  >
                    Browse Trainers
                  </button>
                </div>
              </div>
            </div>

            {/* Notification Modal */}
            {trainer && (
              <TrainerAvailabilityNotificationModal
                isOpen={showNotificationModal}
                onClose={() => setShowNotificationModal(false)}
                trainerId={trainerId}
                trainerName={trainer.name || 'Trainer'}
              />
            )}
          </div>
        </main>
        {!isStudentRoute && <Footer />}
      </>
    );
  }

  return (
    <>
      {!isStudentRoute && <LandingHeader />}
      <main className={`min-h-screen ${isPWA ? 'bg-gradient-to-br from-teal-50 via-blue-50 to-indigo-50' : 'bg-gradient-to-b from-gray-50 to-white'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Back Button */}
          <button
            onClick={() => navigate(-1)}
            className={`flex items-center ${isPWA ? 'text-teal-700 hover:text-teal-800 font-semibold mb-6 px-4 py-2 rounded-lg hover:bg-teal-50 transition-all' : 'text-gray-600 hover:text-primary mb-6 transition-colors'}`}
          >
            <FaArrowLeft className={`mr-2 ${isPWA ? 'text-teal-600' : ''}`} />
            Back
          </button>

          {/* Trainer Profile Card */}
          <div className={`${isPWA ? 'bg-gradient-to-br from-white to-teal-50/30 border-2 border-teal-200/60 shadow-2xl' : 'bg-white border border-gray-200'} rounded-xl sm:rounded-2xl overflow-hidden relative`}>
            {isPWA && (
              <>
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-teal-400 via-blue-400 to-indigo-400"></div>
                <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-teal-100/30 to-blue-100/20 rounded-full blur-3xl"></div>
                <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-br from-blue-100/30 to-indigo-100/20 rounded-full blur-3xl"></div>
              </>
            )}
            <div className={`${isPWA ? 'bg-gradient-to-br from-teal-500 via-blue-500 to-indigo-500' : 'bg-gradient-to-r from-primary to-dark'} p-6 sm:p-8 text-white relative z-10`}>
              <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
                <div className="relative">
                  {isPWA && (
                    <div className="absolute -inset-2 bg-white/20 rounded-full blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  )}
                  <Avatar
                    src={trainer.profileImage}
                    name={trainer.name}
                    size={isPWA ? 140 : 120}
                    className={isPWA ? 'border-4 border-white ring-4 ring-white/30 shadow-2xl' : 'border-4 border-white'}
                    alt={formatName(trainer.name)}
                  />
                </div>
                <div className="flex-1 text-center md:text-left">
                  <h1 className={`${isPWA ? 'text-3xl sm:text-4xl' : 'text-3xl'} font-bold mb-2 drop-shadow-lg`}>
                    {formatName(trainer.name)}
                  </h1>
                  <div className="flex items-center justify-center md:justify-start mb-3">
                    <span className={`inline-block ${isPWA ? 'bg-white/30 backdrop-blur-sm px-4 py-1.5 rounded-xl text-sm font-semibold shadow-lg border border-white/20' : 'bg-white/20 px-3 py-1 rounded-full text-sm font-medium'}`}>
                      {toTitleCase(trainer.service)}
                    </span>
                  </div>
                  <div className="flex items-center justify-center md:justify-start">
                    <StarRating
                      rating={trainer.rating || 0}
                      size="lg"
                      showNumber={true}
                      showReviews={true}
                      reviews={trainer.reviews || 0}
                      className="text-yellow-300"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className={`${isPWA ? 'p-6 sm:p-8' : 'p-8'} relative z-10`}>
              {/* Key Information */}
              <div className={`grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8 ${isPWA ? 'bg-gradient-to-br from-teal-50/50 to-blue-50/30 rounded-xl p-4 sm:p-5 border border-teal-200/50' : 'bg-gray-50 rounded-lg p-4 border border-gray-200'}`}>
                {trainer.yearsOfExperience > 0 && (
                  <div className={`flex items-center ${isPWA ? 'bg-white rounded-lg p-4 shadow-md border border-teal-100/50' : 'bg-white rounded-lg p-4 shadow-sm border border-gray-200'}`}>
                    <div className={`${isPWA ? 'bg-gradient-to-br from-teal-100 to-blue-100 p-3 rounded-xl mr-4 shadow-sm ring-2 ring-teal-200/50' : 'bg-primary/10 p-2 rounded-lg mr-3'}`}>
                      <FaClock className={`${isPWA ? 'text-teal-600' : 'text-primary'} text-xl`} />
                    </div>
                    <div>
                      <p className={`${isPWA ? 'text-xs text-teal-600 font-medium mb-1' : 'text-sm text-gray-500'}`}>Experience</p>
                      <p className={`${isPWA ? 'text-lg font-bold text-teal-900' : 'font-semibold text-gray-900'}`}>{trainer.yearsOfExperience} years</p>
                    </div>
                  </div>
                )}
                {trainer.certification && (
                  <div className={`flex items-center ${isPWA ? 'bg-white rounded-lg p-4 shadow-md border border-teal-100/50' : 'bg-white rounded-lg p-4 shadow-sm border border-gray-200'}`}>
                    <div className={`${isPWA ? 'bg-gradient-to-br from-teal-100 to-blue-100 p-3 rounded-xl mr-4 shadow-sm ring-2 ring-teal-200/50' : 'bg-primary/10 p-2 rounded-lg mr-3'}`}>
                      <FaCertificate className={`${isPWA ? 'text-teal-600' : 'text-primary'} text-xl`} />
                    </div>
                    <div>
                      <p className={`${isPWA ? 'text-xs text-teal-600 font-medium mb-1' : 'text-sm text-gray-500'}`}>Certification</p>
                      <p className={`${isPWA ? 'text-lg font-bold text-teal-900' : 'font-semibold text-gray-900'}`}>{trainer.certification}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Bio */}
              {trainer.bio && (
                <div className={`mb-6 sm:mb-8 ${isPWA ? 'bg-gradient-to-br from-white to-teal-50/30 rounded-xl p-5 sm:p-6 border border-teal-200/50 shadow-md' : ''}`}>
                  <div className="flex items-center gap-2 mb-3">
                    {isPWA && (
                      <div className="relative">
                        <div className="absolute left-0 w-1 h-5 bg-gradient-to-b from-teal-400/40 via-blue-400/40 to-indigo-400/40 rounded-full blur-sm"></div>
                        <div className="relative w-1 h-5 bg-gradient-to-b from-teal-500 via-blue-500 to-indigo-500 rounded-full"></div>
                      </div>
                    )}
                    <h2 className={`${isPWA ? 'text-xl sm:text-2xl font-bold text-teal-900' : 'text-xl font-bold text-gray-900'}`}>About</h2>
                    {isPWA && (
                      <div className="relative">
                        <div className="absolute left-0 w-1 h-5 bg-gradient-to-b from-teal-400/40 via-blue-400/40 to-indigo-400/40 rounded-full blur-sm"></div>
                        <div className="relative w-1 h-5 bg-gradient-to-b from-teal-500 via-blue-500 to-indigo-500 rounded-full"></div>
                      </div>
                    )}
                  </div>
                  <p className={`${isPWA ? 'text-teal-800 leading-relaxed text-base sm:text-lg' : 'text-gray-700 leading-relaxed'}`}>{trainer.bio}</p>
                </div>
              )}

              {/* Teaching Style / Consultation Approach */}
              {trainer.teachingStyle && (
                <div className={`mb-6 sm:mb-8 ${isPWA ? 'bg-gradient-to-br from-white to-blue-50/30 rounded-xl p-5 sm:p-6 border border-blue-200/50 shadow-md' : ''}`}>
                  <div className="flex items-center gap-2 mb-3">
                    {isPWA && (
                      <div className="relative">
                        <div className="absolute left-0 w-1 h-5 bg-gradient-to-b from-blue-400/40 via-indigo-400/40 to-purple-400/40 rounded-full blur-sm"></div>
                        <div className="relative w-1 h-5 bg-gradient-to-b from-blue-500 via-indigo-500 to-purple-500 rounded-full"></div>
                      </div>
                    )}
                    <h2 className={`${isPWA ? 'text-xl sm:text-2xl font-bold text-blue-900' : 'text-xl font-bold text-gray-900'}`}>
                      {trainer.isConsultant ? 'Consultation Approach' : 'Teaching Style'}
                    </h2>
                    {isPWA && (
                      <div className="relative">
                        <div className="absolute left-0 w-1 h-5 bg-gradient-to-b from-blue-400/40 via-indigo-400/40 to-purple-400/40 rounded-full blur-sm"></div>
                        <div className="relative w-1 h-5 bg-gradient-to-b from-blue-500 via-indigo-500 to-purple-500 rounded-full"></div>
                      </div>
                    )}
                  </div>
                  <p className={`${isPWA ? 'text-blue-800 text-base sm:text-lg' : 'text-gray-700'}`}>{trainer.teachingStyle}</p>
                </div>
              )}

              {/* Languages */}
              {trainer.languages && trainer.languages.length > 0 && (
                <div className={`mb-6 sm:mb-8 ${isPWA ? 'bg-gradient-to-br from-white to-indigo-50/30 rounded-xl p-5 sm:p-6 border border-indigo-200/50 shadow-md' : ''}`}>
                  <div className="flex items-center gap-2 mb-3">
                    {isPWA && (
                      <div className="relative">
                        <div className="absolute left-0 w-1 h-5 bg-gradient-to-b from-indigo-400/40 via-purple-400/40 to-pink-400/40 rounded-full blur-sm"></div>
                        <div className="relative w-1 h-5 bg-gradient-to-b from-indigo-500 via-purple-500 to-pink-500 rounded-full"></div>
                      </div>
                    )}
                    <h2 className={`${isPWA ? 'text-xl sm:text-2xl font-bold text-indigo-900' : 'text-xl font-bold text-gray-900'}`}>Languages</h2>
                    {isPWA && (
                      <div className="relative">
                        <div className="absolute left-0 w-1 h-5 bg-gradient-to-b from-indigo-400/40 via-purple-400/40 to-pink-400/40 rounded-full blur-sm"></div>
                        <div className="relative w-1 h-5 bg-gradient-to-b from-indigo-500 via-purple-500 to-pink-500 rounded-full"></div>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2 sm:gap-3">
                    {trainer.languages.map((lang, index) => (
                      <span
                        key={index}
                        className={`inline-flex items-center ${isPWA ? 'px-4 py-2 rounded-xl bg-gradient-to-r from-indigo-100 to-purple-100 text-indigo-700 border border-indigo-200/50 text-sm font-semibold shadow-sm' : 'px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium'}`}
                      >
                        <FaLanguage className={`mr-1.5 ${isPWA ? 'text-indigo-600' : ''}`} />
                        {lang}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Achievements */}
              {trainer.achievements && trainer.achievements.length > 0 && (
                <div className={`mb-6 sm:mb-8 ${isPWA ? 'bg-gradient-to-br from-white to-yellow-50/30 rounded-xl p-5 sm:p-6 border border-yellow-200/50 shadow-md' : ''}`}>
                  <div className="flex items-center gap-2 mb-3">
                    {isPWA && (
                      <div className="relative">
                        <div className="absolute left-0 w-1 h-5 bg-gradient-to-b from-yellow-400/40 via-orange-400/40 to-amber-400/40 rounded-full blur-sm"></div>
                        <div className="relative w-1 h-5 bg-gradient-to-b from-yellow-500 via-orange-500 to-amber-500 rounded-full"></div>
                      </div>
                    )}
                    <h2 className={`${isPWA ? 'text-xl sm:text-2xl font-bold text-yellow-900' : 'text-xl font-bold text-gray-900'}`}>Achievements</h2>
                    {isPWA && (
                      <div className="relative">
                        <div className="absolute left-0 w-1 h-5 bg-gradient-to-b from-yellow-400/40 via-orange-400/40 to-amber-400/40 rounded-full blur-sm"></div>
                        <div className="relative w-1 h-5 bg-gradient-to-b from-yellow-500 via-orange-500 to-amber-500 rounded-full"></div>
                      </div>
                    )}
                  </div>
                  <ul className="space-y-2 sm:space-y-3">
                    {trainer.achievements.map((achievement, index) => (
                      <li key={index} className={`flex items-start ${isPWA ? 'bg-white rounded-lg p-3 shadow-sm border border-yellow-100/50' : 'text-gray-700'}`}>
                        <div className={`${isPWA ? 'bg-gradient-to-br from-yellow-100 to-orange-100 p-2 rounded-lg mr-3 shadow-sm ring-1 ring-yellow-200/50 flex-shrink-0' : ''}`}>
                          <FaMedal className={`${isPWA ? 'text-yellow-600' : 'text-yellow-500'} ${isPWA ? '' : 'mr-2'} mt-1 flex-shrink-0`} />
                        </div>
                        <span className={isPWA ? 'text-yellow-900 font-medium text-base' : ''}>{achievement}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Programs */}
              {trainer.programs && trainer.programs.length > 0 && (
                <div className={`mb-6 sm:mb-8 ${isPWA ? 'bg-gradient-to-br from-white to-cyan-50/30 rounded-xl p-5 sm:p-6 border border-cyan-200/50 shadow-md' : ''}`}>
                  <div className="flex items-center gap-2 mb-4">
                    {isPWA && (
                      <div className="relative">
                        <div className="absolute left-0 w-1 h-5 bg-gradient-to-b from-cyan-400/40 via-teal-400/40 to-blue-400/40 rounded-full blur-sm"></div>
                        <div className="relative w-1 h-5 bg-gradient-to-b from-cyan-500 via-teal-500 to-blue-500 rounded-full"></div>
                      </div>
                    )}
                    <h2 className={`${isPWA ? 'text-xl sm:text-2xl font-bold text-cyan-900' : 'text-xl font-bold text-gray-900'}`}>
                      {trainer.isConsultant ? 'Available Consultation Programs' : 'Available Programs'}
                    </h2>
                    {isPWA && (
                      <div className="relative">
                        <div className="absolute left-0 w-1 h-5 bg-gradient-to-b from-cyan-400/40 via-teal-400/40 to-blue-400/40 rounded-full blur-sm"></div>
                        <div className="relative w-1 h-5 bg-gradient-to-b from-cyan-500 via-teal-500 to-blue-500 rounded-full"></div>
                      </div>
                    )}
                  </div>
                  <div className={`grid grid-cols-1 ${isPWA ? 'sm:grid-cols-2' : 'md:grid-cols-2'} gap-4 sm:gap-5`}>
                    {trainer.programs.map((program) => {
                      // Check if this is a consultant program
                      // Use trainer-level isConsultant flag OR check program-specific fields
                      const isConsultantProgram = trainer.isConsultant || program.partnerType === 'consultant' || program.timeWindowStart || program.slotDurations || Array.isArray(program.slotDurations);

                      // For consultant programs: show time windows and slot durations
                      // For instructor programs: show duration and classes per week
                      let durationText = '';
                      let classesText = '';
                      let timeWindowText = '';
                      let slotDurationsText = '';

                      if (isConsultantProgram) {
                        // Consultant program: show time window if available
                        if (program.timeWindowStart && program.timeWindowEnd) {
                          timeWindowText = `${program.timeWindowStart} - ${program.timeWindowEnd}`;
                        } else {
                          // For consultant programs without time windows, show "By Appointment" or leave empty
                          timeWindowText = 'Available by appointment';
                        }

                        // Show available slot durations if available
                        if (program.slotDurations) {
                          const slots = Array.isArray(program.slotDurations)
                            ? program.slotDurations
                            : Object.keys(program.slotDurations || {});
                          if (slots.length > 0) {
                            slotDurationsText = slots.join(', ');
                          }
                        } else {
                          // For consultant programs without slot durations, don't show duration/classes fields
                          // They will be empty, which is fine
                        }
                      } else {
                        // Instructor program: show duration and classes
                        durationText = program.duration
                          ? `${program.duration} ${program.durationUnit || 'weeks'}`
                          : '';

                        // Calculate sessions per week from classesPerDuration and duration
                        if (program.classesPerDuration && program.duration && program.durationUnit) {
                          // Calculate sessions per week
                          let totalWeeks = 0;
                          if (program.durationUnit === 'weeks') {
                            totalWeeks = program.duration;
                          } else if (program.durationUnit === 'months') {
                            totalWeeks = program.duration * 4.33; // Average weeks per month
                          } else if (program.durationUnit === 'years') {
                            totalWeeks = program.duration * 52; // Weeks per year
                          }

                          if (totalWeeks > 0) {
                            const sessionsPerWeek = Math.round((program.classesPerDuration / totalWeeks) * 10) / 10; // Round to 1 decimal
                            classesText = `${sessionsPerWeek} sessions/week`;
                          } else {
                            // Fallback: show total classes if calculation fails
                            classesText = `${program.classesPerDuration} classes`;
                          }
                        } else if (program.sessions) {
                          // Parse from sessions field if available (from student.controller)
                          const sessionsStr = String(program.sessions);
                          const frequencyMatch = sessionsStr.match(/\((\d+)x\/week\)/);
                          if (frequencyMatch) {
                            classesText = `${frequencyMatch[1]} sessions/week`;
                          } else {
                            // Try to extract just the number before "sessions"
                            const sessionsMatch = sessionsStr.match(/(\d+)\s*sessions?/);
                            if (sessionsMatch) {
                              classesText = `${sessionsMatch[1]} sessions/week`;
                            } else {
                              classesText = sessionsStr;
                            }
                          }
                        }
                      }

                      return (
                        <div
                          key={program.id}
                          onClick={() => handleProgramSelect(program.id)}
                          className={`border-2 ${isPWA ? 'rounded-xl sm:rounded-2xl p-5 sm:p-6' : 'rounded-lg p-5'} cursor-pointer transition-all relative overflow-hidden group ${selectedProgramIds.includes(program.id)
                            ? isPWA ? 'border-teal-400 bg-gradient-to-br from-teal-50 to-blue-50 shadow-xl ring-2 ring-teal-300/50' : 'border-primary bg-primary/5 shadow-lg'
                            : isPWA ? 'border-teal-200/60 bg-white hover:border-teal-400 hover:shadow-xl hover:bg-gradient-to-br hover:from-teal-50/50 hover:to-blue-50/30' : 'border-gray-200 hover:border-primary hover:shadow-md'
                            }`}
                        >
                          {isPWA && (
                            <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-teal-100/30 to-blue-100/20 rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                          )}
                          <div className={`flex items-start justify-between mb-3 ${isPWA ? 'relative z-10' : ''}`}>
                            <h3 className={`font-semibold ${isPWA ? 'text-teal-900 text-xl sm:text-2xl' : 'text-gray-900 text-lg'}`}>{program.name}</h3>
                            {program.isActive && (
                              <span className={`inline-flex items-center ${isPWA ? 'px-3 py-1.5 rounded-xl bg-gradient-to-r from-green-100 to-emerald-100 text-green-700 border border-green-200/50 text-xs font-semibold shadow-sm' : 'px-2 py-1 rounded-full bg-green-100 text-green-800 text-xs font-medium'}`}>
                                <FaCheckCircle className="mr-1" />
                                Active
                              </span>
                            )}
                          </div>

                          {program.description && (
                            <p className={`${isPWA ? 'text-base text-teal-700 mb-3 line-clamp-2 relative z-10' : 'text-sm text-gray-600 mb-3 line-clamp-2'}`}>
                              {program.description}
                            </p>
                          )}

                          {/* Program Details - Different for consultant vs instructor */}
                          <div className={`flex flex-wrap gap-2 sm:gap-3 mb-3 ${isPWA ? 'text-sm text-teal-600 relative z-10' : 'text-xs text-gray-500'}`}>
                            {isConsultantProgram ? (
                              // Consultant program details
                              <>
                                {timeWindowText && (
                                  <div className={`flex items-center ${isPWA ? 'bg-teal-50 px-3 py-1.5 rounded-lg border border-teal-100/50' : ''}`}>
                                    <FaClock className={`mr-1.5 ${isPWA ? 'text-teal-500' : ''}`} />
                                    <span className={isPWA ? 'font-medium' : ''}>{timeWindowText}</span>
                                  </div>
                                )}
                                {slotDurationsText && (
                                  <div className={`flex items-center ${isPWA ? 'bg-blue-50 px-3 py-1.5 rounded-lg border border-blue-100/50' : ''}`}>
                                    <FaBook className={`mr-1.5 ${isPWA ? 'text-blue-500' : ''}`} />
                                    <span className={isPWA ? 'font-medium' : ''}>{slotDurationsText}</span>
                                  </div>
                                )}
                              </>
                            ) : (
                              // Instructor program details
                              <>
                                {durationText && (
                                  <div className={`flex items-center ${isPWA ? 'bg-teal-50 px-3 py-1.5 rounded-lg border border-teal-100/50' : ''}`}>
                                    <FaClock className={`mr-1.5 ${isPWA ? 'text-teal-500' : ''}`} />
                                    <span className={isPWA ? 'font-medium' : ''}>{durationText}</span>
                                  </div>
                                )}
                                {classesText && (
                                  <div className={`flex items-center ${isPWA ? 'bg-blue-50 px-3 py-1.5 rounded-lg border border-blue-100/50' : ''}`}>
                                    <FaBook className={`mr-1.5 ${isPWA ? 'text-blue-500' : ''}`} />
                                    <span className={isPWA ? 'font-medium' : ''}>{classesText}</span>
                                  </div>
                                )}
                              </>
                            )}
                            {program.enrollmentCount !== undefined && program.enrollmentCount > 0 && (
                              <div className={`flex items-center ${isPWA ? 'text-green-700 bg-gradient-to-r from-green-100 to-emerald-100 border border-green-200/50 px-3 py-1.5 rounded-lg font-semibold shadow-sm' : 'text-green-600 bg-green-50 px-2 py-1 rounded-full'}`}>
                                <FaUserTie className="mr-1.5" size={isPWA ? 14 : 10} />
                                <span className={isPWA ? 'text-sm' : 'font-medium'}>{program.enrollmentCount} {program.enrollmentCount === 1 ? 'member' : 'members'} enrolled</span>
                              </div>
                            )}
                          </div>

                          {/* Pricing Information */}
                          <div className={`${isPWA ? 'border-t-2 border-teal-200/60 pt-4 mt-4 relative z-10' : 'border-t border-gray-200 pt-3 mt-3'}`}>
                            {/* For consultant programs, show minimum slot price prominently */}
                            {isConsultantProgram && program.slotPrices && Object.keys(program.slotPrices).length > 0 ? (
                              <div className="space-y-2">
                                {/* Calculate minimum price from slotPrices */}
                                {(() => {
                                  const slotPricesArray = Object.entries(program.slotPrices);
                                  const prices = slotPricesArray.map(([_, price]) => Number(price) || 0);
                                  const minPrice = Math.min(...prices.filter(p => p > 0));
                                  const minPriceEntry = slotPricesArray.find(([_, price]) => Number(price) === minPrice);

                                  return (
                                    <>
                                      {/* Prominent minimum price display */}
                                      <div className="flex items-center justify-between">
                                        <div className="flex items-baseline gap-2">
                                          <span className={`${isPWA ? 'text-lg text-teal-600 font-medium' : 'text-sm text-gray-500'}`}>Starting from</span>
                                          <span className={`${isPWA ? 'text-xl sm:text-2xl font-bold text-teal-700' : 'text-lg font-bold text-primary'}`}>
                                            {formatCurrency(minPrice)}
                                          </span>
                                        </div>
                                      </div>
                                      {/* Optional: Show all pricing details in smaller text below */}
                                      {slotPricesArray.length > 1 && (
                                        <div className="mt-2">
                                          <p className={`${isPWA ? 'text-xs text-teal-600 mb-1' : 'text-xs text-gray-500 mb-1'}`}>All durations:</p>
                                          <div className="flex flex-wrap gap-1.5">
                                            {slotPricesArray.map(([duration, price]) => (
                                              <span key={duration} className={`${isPWA ? 'text-xs text-teal-700 bg-teal-50 px-2 py-0.5 rounded border border-teal-100/50' : 'text-xs text-gray-600 bg-gray-50 px-2 py-0.5 rounded'}`}>
                                                {duration}: {formatCurrency(price)}
                                              </span>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                    </>
                                  );
                                })()}
                              </div>
                            ) : (program.price > 0 || program.currentPrice > 0 || program.basePrice > 0 || program.discountInfo?.discountedPrice > 0 || program.discountInfo?.originalPrice > 0) ? (
                              <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-baseline gap-2">
                                    {program.installmentInfo?.hasInstallmentPlan ? (
                                      <>
                                        <div className="flex flex-col">
                                          <div className="flex items-baseline gap-2">
                                            <span className={`${isPWA ? 'text-base text-teal-400' : 'text-sm text-gray-400'} line-through`}>
                                              {formatCurrency(program.price || program.currentPrice || program.basePrice || 0)}
                                            </span>
                                            <span className={`${isPWA ? 'text-xl sm:text-2xl font-bold text-teal-700' : 'text-lg font-bold text-primary'}`}>
                                              {formatCurrency((program.price || program.currentPrice || program.basePrice || 0) / 2)}
                                            </span>
                                          </div>
                                        </div>
                                      </>
                                    ) : program.discountInfo?.hasDiscount ? (
                                      <>
                                        <span className={`${isPWA ? 'text-base text-teal-400' : 'text-sm text-gray-400'} line-through`}>
                                          {formatCurrency(program.discountInfo.originalPrice)}
                                        </span>
                                        <span className={`${isPWA ? 'text-xl sm:text-2xl font-bold text-teal-700' : 'text-lg font-bold text-primary'}`}>
                                          {formatCurrency(program.discountInfo.discountedPrice)}
                                        </span>
                                      </>
                                    ) : (
                                      <span className={`${isPWA ? 'text-xl sm:text-2xl font-bold text-teal-700' : 'text-lg font-bold text-primary'}`}>
                                        {formatCurrency(program.price || program.currentPrice || program.basePrice || program.discountInfo?.discountedPrice || program.discountInfo?.originalPrice || 0)}
                                      </span>
                                    )}
                                  </div>
                                  {(program.discountInfo?.hasDiscount || program.hasDiscount) && (program.discountInfo?.discountPercentage || program.discountPercentage) > 0 && (
                                    <span className={`inline-flex items-center ${isPWA ? 'px-3 py-1.5 rounded-xl bg-gradient-to-r from-red-100 to-rose-100 text-red-700 border border-red-200/50 text-xs font-semibold shadow-sm' : 'px-2 py-1 rounded-full bg-red-100 text-red-800 text-xs font-medium'}`}>
                                      <FaTag className="mr-1.5" />
                                      {(program.discountInfo?.discountPercentage || program.discountPercentage)}% OFF
                                    </span>
                                  )}
                                </div>
                                {program.installmentInfo?.hasInstallmentPlan && (
                                  <div className={`flex flex-col gap-1 ${isPWA ? 'mt-2' : 'mt-1'}`}>
                                    <div className={`flex items-center ${isPWA ? 'text-sm text-blue-700 bg-gradient-to-r from-blue-100 to-cyan-100 border border-blue-200/50 px-3 py-1.5 rounded-lg w-fit font-semibold shadow-sm' : 'text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded w-fit'}`}>
                                      <FaCreditCard className="mr-1.5" size={isPWA ? 14 : 10} />
                                      <span>Pay in installments available</span>
                                    </div>
                                    <div className={`flex items-center ${isPWA ? 'text-xs text-emerald-700 bg-emerald-50 border border-emerald-200/50 px-2 py-1 rounded-lg w-fit font-bold animate-pulse' : 'text-[10px] text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded w-fit font-bold'}`}>
                                      <FaTag className="mr-1" size={isPWA ? 12 : 8} />
                                      <span>SAVE UPTO 50% WITH ANNUAL PLAN</span>
                                    </div>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="text-sm text-gray-500 italic">
                                Pricing not available
                              </div>
                            )}
                          </div>

                          {/* Select/Deselect Button */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleProgramSelect(program.id);
                            }}
                            className={`w-full mt-3 ${isPWA ? 'py-3 px-4 rounded-xl' : 'py-2 px-4 rounded-lg'} font-medium transition-all duration-300 relative z-10 ${selectedProgramIds.includes(program.id)
                              ? isPWA ? 'bg-gradient-to-r from-teal-500 via-blue-500 to-indigo-500 text-white hover:from-teal-600 hover:via-blue-600 hover:to-indigo-600 shadow-lg' : 'bg-primary text-white hover:bg-primary/90'
                              : isPWA ? 'bg-gradient-to-r from-gray-100 to-gray-200 text-teal-700 hover:from-gray-200 hover:to-gray-300 border border-teal-200/50' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                              }`}
                          >
                            {selectedProgramIds.includes(program.id)
                              ? '✓ Selected (Click to Deselect)'
                              : (isConsultantProgram ? 'Select Consultation' : 'Select Program')}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* CTA Buttons */}
              {selectedProgramIds.length > 0 && (
                <div className={`mb-4 ${isPWA ? 'p-5 bg-gradient-to-br from-teal-50 to-blue-50 rounded-xl border-2 border-teal-200/60 shadow-md' : 'p-4 bg-primary/10 rounded-lg border border-primary/20'}`}>
                  <p className={`${isPWA ? 'text-base text-teal-800 font-semibold mb-2' : 'text-sm text-gray-700 mb-2'}`}>
                    <strong>Selected Program{selectedProgramIds.length > 1 ? 's' : ''}:</strong>
                  </p>
                  <ul className={`list-disc list-inside ${isPWA ? 'text-base text-teal-700' : 'text-sm text-gray-600'}`}>
                    {selectedProgramIds.map((id) => {
                      const program = trainer.programs?.find(p => p.id === id);
                      return program ? (
                        <li key={id}>{program.name}</li>
                      ) : null;
                    })}
                  </ul>
                </div>
              )}

              <div className={`flex flex-col sm:flex-row gap-4 pt-6 ${isPWA ? 'border-t-2 border-teal-200/60' : 'border-t border-gray-200'}`}>
                {isLoggedIn ? (
                  <button
                    onClick={handleEnroll}
                    disabled={selectedProgramIds.length === 0}
                    className={`flex-1 ${isPWA ? 'py-3.5 px-6 rounded-xl sm:rounded-2xl font-bold shadow-lg hover:shadow-xl' : 'py-3 px-6 rounded-lg font-medium'} transition-all duration-300 flex items-center justify-center ${selectedProgramIds.length > 0
                      ? isPWA ? 'bg-gradient-to-r from-teal-500 via-blue-500 to-indigo-500 text-white hover:from-teal-600 hover:via-blue-600 hover:to-indigo-600' : 'bg-primary text-white hover:bg-primary/90'
                      : isPWA ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      }`}
                  >
                    <FaBook className="mr-2" />
                    {selectedProgramIds.length > 0
                      ? (trainer.isConsultant
                        ? `Book ${selectedProgramIds.length} Consultation${selectedProgramIds.length > 1 ? 's' : ''}`
                        : `Enroll in ${selectedProgramIds.length} Program${selectedProgramIds.length > 1 ? 's' : ''}`)
                      : (trainer.isConsultant ? 'Select a Consultation Program to Book' : 'Select a Program to Enroll')}
                  </button>
                ) : (
                  <>
                    <button
                      onClick={handleEnroll}
                      disabled={selectedProgramIds.length === 0}
                      className={`flex-1 ${isPWA ? 'py-3.5 px-6 rounded-xl sm:rounded-2xl font-bold shadow-lg hover:shadow-xl' : 'py-3 px-6 rounded-lg font-medium'} transition-all duration-300 flex items-center justify-center ${selectedProgramIds.length > 0
                        ? isPWA ? 'bg-gradient-to-r from-teal-500 via-blue-500 to-indigo-500 text-white hover:from-teal-600 hover:via-blue-600 hover:to-indigo-600' : 'bg-primary text-white hover:bg-primary/90'
                        : isPWA ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        }`}
                    >
                      <FaUserPlus className="mr-2" />
                      {selectedProgramIds.length > 0
                        ? (trainer.isConsultant
                          ? `Sign Up to Book (${selectedProgramIds.length})`
                          : `Sign Up to Enroll (${selectedProgramIds.length})`)
                        : (trainer.isConsultant ? 'Select a Consultation Program First' : 'Select a Program First')}
                    </button>
                    <button
                      onClick={() => {
                        const params = new URLSearchParams();
                        params.set('trainerId', trainerId || '');
                        // Include selected program IDs if any are selected
                        selectedProgramIds.forEach((id) => {
                          params.append('programId', id);
                        });
                        navigate(`/login?${params.toString()}`);
                      }}
                      className={`flex-1 ${isPWA ? 'bg-gradient-to-r from-gray-200 to-gray-300 text-teal-800 py-3.5 px-6 rounded-xl sm:rounded-2xl font-bold shadow-md hover:shadow-lg hover:from-gray-300 hover:to-gray-400' : 'bg-gray-200 text-gray-800 py-3 px-6 rounded-lg font-medium hover:bg-gray-300'} transition-all duration-300 flex items-center justify-center`}
                    >
                      <FaSignInAlt className="mr-2" />
                      Sign In
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
      {!isStudentRoute && <Footer />}

      {/* Booking Modal for Consultant Programs */}
      {showBookingModal && bookingProgram && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="bg-gradient-to-r from-teal-600 via-blue-600 to-indigo-600 text-white p-6 rounded-t-xl">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold">Book Consultation</h2>
                  <p className="text-white/90 text-sm mt-1">{bookingProgram.name}</p>
                </div>
                <button
                  onClick={() => setShowBookingModal(false)}
                  className="text-white/80 hover:text-white transition-colors"
                >
                  <FaTimesCircle className="text-2xl" />
                </button>
              </div>
            </div>

            {/* Form */}
            <div className="p-6 space-y-4">
              {/* Slot Duration Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Slot Duration *
                </label>
                {(() => {
                  // For consultant programs, always show dropdown with available slot durations
                  let availableDurations = [];

                  // Debug logging (remove in production)
                  if (import.meta.env.DEV) {
                    console.log('[Booking Modal] Program data:', {
                      slotPrices: bookingProgram.slotPrices,
                      slotDurations: bookingProgram.slotDurations,
                      allProgramKeys: Object.keys(bookingProgram),
                      program: bookingProgram
                    });
                  }

                  // Priority 1: Check slotPrices (object with duration as keys and price as values)
                  // slotPrices format: { "30 mins": 500, "1 hour": 1000, "2 hours": 2000 }
                  if (bookingProgram.slotPrices && typeof bookingProgram.slotPrices === 'object' && Object.keys(bookingProgram.slotPrices).length > 0) {
                    availableDurations = Object.entries(bookingProgram.slotPrices).map(([duration, price]) => [duration, price]);
                  }
                  // Priority 2: Check slotDurations array
                  // slotDurations format: ["30 mins", "1 hour", "2 hours"]
                  else if (bookingProgram.slotDurations && Array.isArray(bookingProgram.slotDurations) && bookingProgram.slotDurations.length > 0) {
                    // If slotPrices exists but is empty, try to match with slotDurations to get prices
                    const slotPricesObj = (bookingProgram.slotPrices && typeof bookingProgram.slotPrices === 'object') ? bookingProgram.slotPrices : {};
                    availableDurations = bookingProgram.slotDurations.map(duration => [duration, slotPricesObj[duration] || null]);
                  }
                  // Priority 3: Check slotDurations as object
                  else if (bookingProgram.slotDurations && typeof bookingProgram.slotDurations === 'object' && !Array.isArray(bookingProgram.slotDurations)) {
                    availableDurations = Object.entries(bookingProgram.slotDurations);
                  }

                  // Always show dropdown for consultant programs (even if no durations found, show empty dropdown with message)
                  return (
                    <select
                      value={selectedSlotDuration}
                      onChange={(e) => setSelectedSlotDuration(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                      required
                      disabled={availableDurations.length === 0}
                    >
                      <option value="">
                        {availableDurations.length > 0 ? 'Select duration' : 'No durations available - Please contact consultant'}
                      </option>
                      {availableDurations.map(([duration, price]) => (
                        <option key={duration} value={duration}>
                          {duration} {price && Number(price) > 0 ? `- ${formatCurrency(price)}` : ''}
                        </option>
                      ))}
                    </select>
                  );
                })()}
              </div>

              {/* Date Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date *
                </label>
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => {
                    setSelectedDate(e.target.value);
                    setSelectedTime(''); // Reset time when date changes
                    setTimeConflict(null); // Clear conflict when date changes
                  }}
                  min={(() => {
                    const now = new Date();
                    if (bookingProgram?.timeWindowEnd) {
                      const [endHours, endMinutes] = bookingProgram.timeWindowEnd.split(':').map(Number);
                      const endTime = new Date(now);
                      endTime.setHours(endHours, endMinutes, 0, 0);
                      if (now > endTime) {
                        const tomorrow = new Date(now);
                        tomorrow.setDate(tomorrow.getDate() + 1);
                        return tomorrow.toISOString().split('T')[0];
                      }
                    }
                    return now.toISOString().split('T')[0];
                  })()}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  required
                />
                {selectedDate && new Date(selectedDate) < new Date(new Date().toISOString().split('T')[0]) && (
                  <p className="text-xs text-red-600 mt-1">Please select a future date</p>
                )}
              </div>

              {/* Time Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Time *
                </label>
                {bookingProgram.timeWindowStart && bookingProgram.timeWindowEnd ? (
                  <div>
                    <input
                      type="time"
                      value={selectedTime}
                      onChange={(e) => setSelectedTime(e.target.value)}
                      min={bookingProgram.timeWindowStart}
                      max={bookingProgram.timeWindowEnd}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${timeConflict ? 'border-red-300 bg-red-50' : 'border-gray-300'
                        }`}
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Available between {bookingProgram.timeWindowStart} - {bookingProgram.timeWindowEnd}
                    </p>
                    {selectedTime && selectedDate && (() => {
                      const selectedDateTime = new Date(`${selectedDate}T${selectedTime}`);
                      const now = new Date();
                      if (selectedDateTime < now) {
                        return <p className="text-xs text-red-600 mt-1">Please select a future time</p>;
                      }
                      return null;
                    })()}
                  </div>
                ) : (
                  <div>
                    <input
                      type="time"
                      value={selectedTime}
                      onChange={(e) => setSelectedTime(e.target.value)}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${timeConflict ? 'border-red-300 bg-red-50' : 'border-gray-300'
                        }`}
                      required
                    />
                    {selectedTime && selectedDate && (() => {
                      const selectedDateTime = new Date(`${selectedDate}T${selectedTime}`);
                      const now = new Date();
                      if (selectedDateTime < now) {
                        return <p className="text-xs text-red-600 mt-1">Please select a future time</p>;
                      }
                      return null;
                    })()}
                  </div>
                )}
              </div>

              {/* Time Conflict Warning */}
              {timeConflict && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <FaTimesCircle className="text-red-600 mr-2 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-red-800">{timeConflict.message}</p>
                      {timeConflict.nextAvailable && (
                        <button
                          onClick={() => setSelectedTime(timeConflict.nextAvailable)}
                          className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
                        >
                          Use suggested time: {timeConflict.nextAvailable}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Loading appointments indicator */}
              {loadingAppointments && selectedDate && (
                <div className="text-xs text-gray-500 flex items-center">
                  <FaSpinner className="animate-spin mr-2" />
                  Checking for available time slots...
                </div>
              )}

              {/* Price Display */}
              {selectedSlotDuration && bookingProgram.slotPrices && bookingProgram.slotPrices[selectedSlotDuration] && (
                <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-700">Total Price:</span>
                    <span className="text-xl font-bold text-teal-700">
                      {formatCurrency(bookingProgram.slotPrices[selectedSlotDuration])}
                    </span>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setShowBookingModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleBookAppointment}
                  disabled={
                    isBooking ||
                    !selectedSlotDuration ||
                    !selectedDate ||
                    !selectedTime ||
                    (selectedDate && selectedTime && new Date(`${selectedDate}T${selectedTime}`) < new Date())
                  }
                  className="flex-1 px-4 py-2 bg-gradient-to-r from-teal-600 to-blue-600 text-white rounded-lg font-medium hover:from-teal-700 hover:to-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {isBooking ? (
                    <>
                      <FaSpinner className="animate-spin mr-2" />
                      Booking...
                    </>
                  ) : (
                    <>
                      <FaCalendarAlt className="mr-2" />
                      Book Appointment
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
      {/* Quick Profile Completion Modal */}
      {showQuickProfileModal && createPortal(
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/70 backdrop-blur-md p-4">
          <div className={`bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col ${isPWA ? 'border-2 border-teal-200' : ''}`}>
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-teal-600 via-blue-600 to-indigo-600 text-white p-6 relative overflow-hidden">
               <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-16 translate-x-16 blur-2xl"></div>
               <div className="relative z-10">
                 <div className="flex items-center justify-between mb-2">
                   <h2 className="text-xl sm:text-2xl font-bold">One Final Step!</h2>
                   <button onClick={() => setShowQuickProfileModal(false)} className="text-white/80 hover:text-white transition-colors">
                     <FaTimes className="text-xl" />
                   </button>
                 </div>
                 <p className="text-white/90 text-sm small">Please provide your basic details to complete your enrollment. This helps your trainer prepare for your sessions.</p>
               </div>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto space-y-5">
               {/* Phone */}
               <div className="space-y-1.5">
                 <label className="text-xs font-bold text-teal-800 uppercase tracking-wider block">Phone Number *</label>
                 <div className="relative">
                   <FaPhone className="absolute left-3 top-1/2 -translate-y-1/2 text-teal-500" />
                   <input 
                     type="tel" 
                     value={quickProfileData.mobile}
                     onChange={(e) => setQuickProfileData({...quickProfileData, mobile: e.target.value.replace(/[^0-9]/g, '').slice(0, 10)})}
                     placeholder="10-digit mobile number"
                     className="w-full pl-10 pr-4 py-2.5 bg-teal-50/30 border-2 border-teal-100 rounded-xl focus:ring-2 focus:ring-teal-400 focus:border-transparent transition-all outline-none"
                   />
                 </div>
               </div>

               {/* Gender */}
               <div className="space-y-1.5">
                 <label className="text-xs font-bold text-teal-800 uppercase tracking-wider block">Gender *</label>
                 <div className="grid grid-cols-3 gap-3">
                   {['male', 'female', 'other'].map(g => (
                     <button
                       key={g}
                       onClick={() => setQuickProfileData({...quickProfileData, gender: g})}
                       className={`py-2 px-3 rounded-lg border-2 text-sm font-medium transition-all ${quickProfileData.gender === g 
                         ? 'bg-teal-600 border-teal-600 text-white shadow-md shadow-teal-200' 
                         : 'bg-white border-teal-100 text-teal-700 hover:border-teal-300'}`}
                     >
                       {toTitleCase(g)}
                     </button>
                   ))}
                 </div>
               </div>

               {/* Address */}
               <div className="space-y-4 pt-2 border-t border-teal-50">
                 <label className="text-xs font-bold text-teal-800 uppercase tracking-wider block flex items-center">
                   <FaHome className="mr-2" /> Address Details *
                 </label>
                 
                 <div className="grid grid-cols-1 gap-4">
                   <div className="space-y-1">
                     <input 
                       placeholder="Address Line 1"
                       value={quickProfileData.addressLine1}
                       onChange={(e) => setQuickProfileData({...quickProfileData, addressLine1: e.target.value})}
                       className="w-full px-4 py-2.5 bg-teal-50/30 border-2 border-teal-100 rounded-xl focus:ring-2 focus:ring-teal-400 focus:border-transparent outline-none"
                     />
                   </div>
                   
                   <div className="grid grid-cols-2 gap-4">
                     <div className="relative">
                       <FaCity className="absolute left-3 top-1/2 -translate-y-1/2 text-teal-500" />
                       <input 
                         placeholder="City"
                         value={quickProfileData.city}
                         onChange={(e) => setQuickProfileData({...quickProfileData, city: e.target.value})}
                         className="w-full pl-10 pr-4 py-2.5 bg-teal-50/30 border-2 border-teal-100 rounded-xl focus:ring-2 focus:ring-teal-400 focus:border-transparent outline-none"
                       />
                     </div>
                     <div className="relative">
                       <FaMapMarkerAlt className="absolute left-3 top-1/2 -translate-y-1/2 text-teal-500" />
                       <input 
                         placeholder="State"
                         value={quickProfileData.state}
                         onChange={(e) => setQuickProfileData({...quickProfileData, state: e.target.value})}
                         className="w-full pl-10 pr-4 py-2.5 bg-teal-50/30 border-2 border-teal-100 rounded-xl focus:ring-2 focus:ring-teal-400 focus:border-transparent outline-none"
                       />
                     </div>
                   </div>

                   <div className="w-1/2">
                     <input 
                       placeholder="Pincode"
                       value={quickProfileData.pincode}
                       onChange={(e) => setQuickProfileData({...quickProfileData, pincode: e.target.value.replace(/[^0-9]/g, '').slice(0, 6)})}
                       className="w-full px-4 py-2.5 bg-teal-50/30 border-2 border-teal-100 rounded-xl focus:ring-2 focus:ring-teal-400 focus:border-transparent outline-none"
                     />
                   </div>
                 </div>
               </div>
            </div>

            {/* Modal Footer */}
            <div className="p-6 bg-gray-50 border-t border-gray-100">
               <button
                 disabled={isUpdatingProfile}
                 onClick={async () => {
                   // Validation
                   if (!quickProfileData.mobile || quickProfileData.mobile.length < 10) return toast.error('Valid phone number is required');
                   if (!quickProfileData.addressLine1 || !quickProfileData.city || !quickProfileData.state || !quickProfileData.pincode) return toast.error('Address details are required');
                   
                   try {
                     setIsUpdatingProfile(true);
                     const updateData = {
                       mobile: quickProfileData.mobile,
                       gender: quickProfileData.gender,
                       address: {
                         addressLine1: quickProfileData.addressLine1,
                         addressLine2: '',
                         city: quickProfileData.city,
                         state: quickProfileData.state,
                         pincode: quickProfileData.pincode,
                         country: 'India'
                       }
                     };
                     
                     const response = await HttpInterceptor.post('/api/students/profile/update', updateData);
                     
                     if (response.status === 200) {
                        // Update local context
                        setSession(prev => ({
                          ...prev,
                          ...updateData
                        }));
                        
                        // Refresh cache
                        mutate('/auth/session');
                        
                        setShowQuickProfileModal(false);
                        toast.success('Details updated! Completing your enrollment...');
                        
                        // Automatically trigger enrollment now that profile is done
                        handleEnroll();
                     }
                   } catch (err) {
                     toast.error('Failed to update details. Please try again.');
                   } finally {
                     setIsUpdatingProfile(false);
                   }
                 }}
                 className="w-full py-4 px-6 bg-gradient-to-r from-teal-600 via-blue-600 to-indigo-600 text-white rounded-xl font-bold shadow-xl hover:shadow-teal-200/50 hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-3 disabled:opacity-70"
               >
                 {isUpdatingProfile ? (
                   <FaSpinner className="animate-spin text-xl" />
                 ) : (
                   <>
                     <FaSave className="text-xl" />
                     <span>Finish Profile & Enroll Now</span>
                   </>
                 )}
               </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

export default PublicTrainerProfile;

